import { useEffect, useState } from 'react';
import axios from 'axios';
import { DEPARTMENTS as STATIC_DEPARTMENTS } from '@/constants/departments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let cache = null;
let cachePromise = null;

/**
 * Hook that returns the live list of departments from the database, falling
 * back to the static list while loading or on error. Cached in-module so the
 * API is hit at most once per page load.
 *
 * Returns: [departments, { loading, refresh }]
 */
export default function useDepartments() {
  const [list, setList] = useState(cache || STATIC_DEPARTMENTS);
  const [loading, setLoading] = useState(!cache);

  const fetchOnce = () => {
    if (cachePromise) return cachePromise;
    cachePromise = axios.get(`${API}/departments`, { withCredentials: true })
      .then(({ data }) => {
        if (Array.isArray(data) && data.length > 0) {
          cache = data;
          return data;
        }
        return STATIC_DEPARTMENTS;
      })
      .catch(() => STATIC_DEPARTMENTS)
      .finally(() => { cachePromise = null; });
    return cachePromise;
  };

  useEffect(() => {
    let alive = true;
    fetchOnce().then((data) => {
      if (alive) {
        setList(data);
        setLoading(false);
      }
    });
    return () => { alive = false; };
  }, []);

  const refresh = async () => {
    cache = null;
    cachePromise = null;
    setLoading(true);
    const data = await fetchOnce();
    setList(data);
    setLoading(false);
  };

  return [list, { loading, refresh }];
}
